import { FormikProps } from "formik";

import { nameof } from "@/common/helper/commonHelper";

import { TemplateFormValues } from "../TemplateManagement.types";

// Fields that accept placeholder insertion; the template title is deliberately not a target.
const InsertTargetFields: readonly string[] = [
  nameof<TemplateFormValues>("subject"),
  nameof<TemplateFormValues>("content"),
];

const isInsertTarget = (element: Element | null): element is HTMLInputElement | HTMLTextAreaElement =>
  (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) &&
  InsertTargetFields.includes(element.name);

/**
 * Inserts placeholder tokens at the cursor position of the currently focused template field.
 * The panel's chips keep that field focused by preventing their mousedown default, so at click
 * time document.activeElement is still the field owning the cursor. A click while no eligible
 * field is focused does nothing — insertion is defined relative to a cursor.
 */
export function usePlaceholderInsert(formik: FormikProps<TemplateFormValues>) {
  const insertPlaceholder = (token: string) => {
    const input = document.activeElement;
    if (!isInsertTarget(input)) {
      return;
    }

    // The input is controlled, so its DOM value equals the Formik value, and its name attribute
    // is the Formik field name (both set by FormikTextField).
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    void formik.setFieldValue(input.name, input.value.slice(0, start) + token + input.value.slice(end));

    // Place the caret behind the inserted token — after the controlled input has re-rendered
    // with the new value (the re-render resets the caret to the end).
    requestAnimationFrame(() => {
      const caret = start + token.length;
      input.setSelectionRange(caret, caret);
    });
  };

  return { insertPlaceholder };
}
